import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../components/buttons/button";
import Dropdown from "../../../components/dropdown/dropdown";
import { SearchInput } from "../../../components/inputs/searchInput";
import { Pill } from "../../../components/pill";
import { TableComponent } from "../../../components/table";
import { BgWrapper } from "../../../components/wrapper/bgWrapper";
import { formatToCurrency } from "../../../utils/helpers";
import {
  EarningsSummaryData,
  EventSales,
  QueryParams,
} from "../../../utils/types";
import { useAppSelector } from "../../../redux/hooks";

export const SalesOverviewPage = ({
  fetchingOverview,
  fetchingSummary,
  setQuery,
  overviewData,
  query,
  earningSummary,
}: {
  query: QueryParams;
  fetchingOverview: boolean;
  fetchingSummary: boolean;
  setQuery: (query: QueryParams) => void;
  overviewData?: {
    data: {
      totalCount: number;
      isNextPage: boolean;
      page: number;
      limit: number;
      data: EventSales[];
    };
  };
  earningSummary?: EarningsSummaryData;
}) => {
  const [year, setYear] = useState<string>("October 2025");
  const navigate = useNavigate();
  const { salesProfile } = useAppSelector((state) => state.auth);

  const dropDownOptions = [
    {
      label: "October 2025",
      value: "October 2025",
      onClick: () => {
        setYear("October 2025");
      },
    },
  ];
  return (
    <div className="mt-3 flex flex-col gap-3">
      <BgWrapper className="relative flex w-full flex-col gap-4">
        <div className="flex w-full justify-between">
          <div className="flex flex-col gap-2">
            <p className="text-sm text-grey-600">Earnings (This Month)</p>
            <p className="text-[32px] font-extrabold leading-8 text-grey-950">
              {fetchingSummary
                ? "₦---"
                : formatToCurrency(earningSummary?.monthlyEarningsNGN ?? 0, {
                    currency: "NGN",
                  })}
            </p>
            <p className="text-base font-bold text-grey-600">
              {fetchingSummary
                ? "Getting your sales earnings"
                : `${earningSummary?.monthlyTicketsSold || "No"} Tickets Sold`}
            </p>
            <p className="mt-3 text-base font-medium text-grey-500">
              {`Earn ${formatToCurrency(salesProfile?.commission?.NGN ?? 0, {
                currency: "NGN",
              })}  whenever organizers who sign up with your link sell paid tickets! 🤑✨`}
            </p>
          </div>
          <div className="absolute right-5 top-0 hidden w-fit md:block">
            <img src="/assets/guest.svg" alt="" />
          </div>
        </div>
      </BgWrapper>
      <BgWrapper className="flex flex-col gap-3 rounded-t-2xl">
        <div className="flex w-full flex-col items-center justify-between gap-4 md:flex-row">
          <SearchInput
            searchFunction={(value) => {
              setQuery({
                ...query,
                search: value,
                page: 1,
              });
            }}
            placeholder="Search"
            className="md:!max-w-[300px]"
          />
          <Dropdown
            menuItems={dropDownOptions.map((option) => {
              return {
                name: (
                  <p className="soft-shrink flex items-center gap-2 !rounded-md !bg-white p-3 text-left !font-bold">
                    {option.label}
                  </p>
                ),
                onClick: option.onClick,
              };
            })}
            className=""
            menuClassName="!gap-1 !border-0 !bg-grey-50 !p-1 !w-[200px]"
            menuItemsClassName="border-none !text-base !bg-transparent !p-0"
            position="bottom end"
          >
            <Button
              text={year}
              icon="caret-down"
              type="button"
              iconFirst={false}
              variation="styled-grey"
              iconClassName={"mb-1"}
              className="max-w-fit"
            />
          </Dropdown>
        </div>
        <TableComponent
          headers={[]}
          rows={
            overviewData?.data?.data?.map((event, idx) => {
              return {
                id: idx,
                items: [
                  <div
                    className={`flex w-full flex-1 flex-col justify-between gap-2 md:flex-row md:items-center`}
                  >
                    <div className="flex items-center gap-2">
                      <img
                        src={event.image.replace("http:", "https:")}
                        alt="event banner"
                        className="block h-[40px] w-[40px] overflow-hidden rounded-sm object-cover"
                      />{" "}
                      <div className="flex items-center gap-[2px]">
                        <p className="text-sm font-bold text-grey-900">
                          {event.eventName}
                        </p>
                        <div>{event.currency === "NGN" ? "🇳🇬" : "🇨🇦"}</div>
                        <p className="text-sm text-grey-600">
                          ∙ {event.ticketsSold || "No"} Ticket
                          {event.ticketsSold > 1 && "s"} Sold (
                          {formatToCurrency(event?.totalCommission, {
                            currency: "NGN",
                          })}
                          )
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Pill
                        text={`${event?.status === "upcoming" ? "UPCOMING" : event?.status === "past" ? "PAST" : "DRAFT"}`}
                        variation={`${event?.status === "upcoming" ? "blue" : "grey"}`}
                      />
                      <p className="text-sm text-grey-600">
                        Created by {event.organizer}
                      </p>
                    </div>
                  </div>,
                ],
              };
            }) ?? []
          }
          loadingRows={query.limit}
          isLoading={fetchingOverview}
          isRowClickable={true}
          onRowClick={(idx) => {
            const eventId = overviewData?.data?.data?.[idx]?._id;
            const slug = overviewData?.data?.data?.[idx]?.slug;
            if (eventId && slug) {
              navigate(
                `/dashboard/sales/users/${eventId}/${encodeURIComponent(slug)}`,
              );
            }
          }}
          isTableEmpty={[0, undefined].includes(
            overviewData?.data?.data?.length,
          )}
          emptyTitle="No Sales Yet"
          emptySubTitle="Share the referral link with event organizers to get started!"
          emptyImage="/assets/images/guest-glass.png"
          isPaginated
          currentPage={query.page ?? 1}
          totalCount={overviewData?.data?.totalCount ?? 0}
          itemsPerPage={query.limit!}
          pageRangeDisplayed={3}
          onPageChange={(page) => {
            setQuery({
              ...query,
              page: page,
            });
          }}
          className=""
        />
      </BgWrapper>
    </div>
  );
};
